import React from 'react';
import { Form, FormikProvider } from 'formik';
import { Autocomplete, Box, Chip, FormControl, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import GlobalSelectField from '../../components/mui/GlobalSelectField';
import { CancelButton, SubmitButton } from '../../components/mui/Buttons/Buttons';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { id } from 'date-fns/locale';

const BatchForm = ({
  formik,
  loading,
  setOpen,
  courseData,
  subjects,
  classData,
  categories,
  syllabus,
  offlineCenters,
  row,
}) => {
  const showError = (field) => (formik.touched[field] || formik.submitCount > 0) && Boolean(formik.errors[field]);

  const classmodes = [
    { id: 1, value: 'Online' },
    { id: 2, value: 'Offline' },
    { id: 3, value: 'Hybrid' },
  ];

  const profile = JSON.parse(localStorage.getItem('cred')).profile;

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Box>
          <Stack direction={'column'} spacing={2}>
            <TextField
              name="course_batch_name"
              value={formik.values.course_batch_name}
              error={formik.touched.course_batch_name && Boolean(formik.errors.course_batch_name)}
              helperText={formik.errors.course_batch_name}
              onChange={formik.handleChange}
              fullWidth
              label="Batch Name"
            />
            {profile?.departmentId === 35 && (
              <GlobalSelectField
                options={{
                  formik,
                  name: 'class_mode',
                  label: 'Class Mode',
                  data: classmodes,
                  keys: {
                    id: 'id',
                    value: 'value',
                  },
                  sx: { width: '100%' },
                }}
              />
            )}
            {profile?.departmentId === 35 && formik.values.class_mode === 2 && (
              <GlobalSelectField
                options={{
                  formik,
                  name: 'offline_center',
                  label: 'Center',
                  data: offlineCenters,
                  keys: {
                    id: 'offline_center_id',
                    value: 'center_name',
                  },
                  sx: { width: '100%' },
                }}
              />
            )}
            <Autocomplete
              fullWidth
              name="course_available_id"
              options={courseData || []}
              getOptionLabel={(option) => option?.course_name || ''}
              value={
                courseData.find((course) => course.course_available_id === formik.values.course_available_id) || null
              }
              onChange={(e, value) => {
                formik.setFieldValue('course_available_id', value?.course_available_id || '');
              }}
              renderInput={(params) => (
                <TextField
                  disabled
                  fullWidth
                  autoComplete="off"
                  {...params}
                  label="Course"
                  error={formik.touched.course_available_id && Boolean(formik.errors.course_available_id)}
                  helperText={formik.touched.course_available_id && formik.errors.course_available_id}
                />
              )}
            />

            <Autocomplete
              fullWidth
              multiple
              name="subjects"
              options={subjects}
              getOptionLabel={(option) => option.subjectName || ''}
              value={formik.values.subjects}
              isOptionEqualToValue={(option, value) => option.subjectId === value.subjectId}
              onChange={(e, value) => {
                const updated = value.map((subject) => {
                  const existing = formik.values.subjects.find((s) => s.subjectId === subject.subjectId);
                  return existing || { ...subject, priority: '' };
                });
                formik.setFieldValue('subjects', updated);
              }}
              renderInput={(params) => (
                <TextField
                  disabled
                  fullWidth
                  autoComplete="off"
                  {...params}
                  label="Subject"
                  error={formik.touched.subjects && Boolean(formik.errors.subjects)}
                  helperText={formik.touched.subjects && formik.errors.subjects}
                />
              )}
            />
            {profile?.departmentId !== 35 && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {formik.values.subjects.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Chip label="Priorities" variant="outlined" color="secondary" />
                  </Box>
                )}
                {formik.values.subjects.map((subject, index) => (
                  <Box
                    key={subject.subjectId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'row',
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <Typography>{subject.subjectName}</Typography>
                    <FormControl>
                      <Select
                        size="small"
                        label="Priority"
                        value={subject.priority}
                        disabled={subject.priority !== ''}
                        onChange={(e) => {
                          const updated = [...formik.values.subjects];
                          updated[index].priority = Number(e.target.value);

                          let usedPriorities = updated.map((s) => s.priority).filter((p) => p !== '' && p !== null);

                          let nextPriority = 1;
                          updated.forEach((s) => {
                            if (!s.priority) {
                              while (usedPriorities.includes(nextPriority)) {
                                nextPriority++;
                              }
                              s.priority = nextPriority;
                              usedPriorities.push(nextPriority);
                            }
                          });

                          formik.setFieldValue('subjects', updated);
                        }}
                      >
                        <MenuItem disabled>Select Priority</MenuItem>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <MenuItem
                            key={num}
                            value={num}
                            disabled={formik.values.subjects.some((s, i) => i !== index && s.priority === num)}
                          >
                            {num}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ))}
              </Stack>
            )}

            <TextField
              name="course_batch_capacity"
              type="number"
              value={formik.values.course_batch_capacity}
              error={formik.touched.course_batch_capacity && Boolean(formik.errors.course_batch_capacity)}
              helperText={formik.errors.course_batch_capacity}
              onChange={formik.handleChange}
              fullWidth
              label="Capacity"
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                name="course_batch_start_date"
                value={formik.values.course_batch_start_date}
                onChange={(value) => formik.setFieldValue('course_batch_start_date', value)}
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: showError('course_batch_start_date'),
                    helperText: showError('course_batch_start_date') && formik.errors.course_batch_start_date,
                  },
                }}
              />
            </LocalizationProvider>
            {profile?.departmentId !== 35 && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <GlobalSelectField
                  options={{
                    formik,
                    name: 'course_batch_class_id',
                    label: 'Class/Category',
                    data: classData,
                    keys: {
                      id: 'classId',
                      value: 'className',
                    },
                    sx: { width: '100%' },
                  }}
                />
                <GlobalSelectField
                  options={{
                    formik,
                    name: 'course_category',
                    label: 'Level (For Foundation)',
                    data: categories,
                    keys: {
                      id: 'id',
                      value: 'levelName',
                    },
                    sx: { width: '100%' },
                  }}
                />
              </Stack>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  label="Start Time"
                  name="course_batch_start_time"
                  value={formik.values.course_batch_start_time}
                  onChange={(value) => formik.setFieldValue('course_batch_start_time', value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: showError('course_batch_start_time'),
                      helperText: showError('course_batch_start_time') && formik.errors.course_batch_start_time,
                    },
                  }}
                />

                <TimePicker
                  label="End Time"
                  name="course_batch_end_time"
                  value={formik.values.course_batch_end_time}
                  onChange={(value) => formik.setFieldValue('course_batch_end_time', value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: showError('course_batch_end_time'),
                      helperText: showError('course_batch_end_time') && formik.errors.course_batch_end_time,
                    },
                  }}
                />
              </LocalizationProvider>
            </Stack>
            {profile?.departmentId !== 35 && (
              <GlobalSelectField
                options={{
                  formik,
                  name: 'course_batch_syllabus_id',
                  label: 'Syllabus',
                  data: syllabus,
                  keys: {
                    id: 'id',
                    value: 'name',
                  },
                }}
              />
            )}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
            <Stack direction={'row'} spacing={1}>
              <CancelButton size="small" action={() => setOpen(false)}>
                Cancel
              </CancelButton>
              <SubmitButton disabled={loading} size="small" type="submit">
                {loading ? 'Please wait...' : 'Save'}
              </SubmitButton>
            </Stack>
          </Box>
        </Box>
      </Form>
    </FormikProvider>
  );
};

export default BatchForm;
